import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/financial-cycles';

const monthNames = ['January','February','March','April','May','June','July',
                    'August','September','October','November','December'];

function FinancialCycles() {
  const [cycles, setCycles]               = useState([]);
  const [roscaCycles, setRoscaCycles]     = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [obligations, setObligations]     = useState([]);
  const [view, setView]                   = useState('list');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showNewCycle, setShowNewCycle]   = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [editingCycleId, setEditingCycleId]     = useState(null);
  const [editCycleData, setEditCycleData]       = useState({});
  const [msg, setMsg]                     = useState('');
  const [msgType, setMsgType]             = useState('success');
  const [loading, setLoading]             = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const payload  = token ? JSON.parse(atob(token.split('.')[1])) : {};
  const role     = payload.role || 'member';
  const canWrite = role === 'admin' || role === 'finance';

  const [cycleForm, setCycleForm] = useState({
    cycle_number: '', cycle_name: '', start_date: '', rosca_cycle_id: ''
  });

  const [scheduleForm, setScheduleForm] = useState({
    month_number: '', meeting_date: '', savings_due_date: '',
    rosca_due_date: '', subscription_due_date: '', notes: ''
  });

  useEffect(() => {
    fetchCycles();
    axios.get(`${API}/rosca/cycles`, { headers }).then(r => setRoscaCycles(r.data)).catch(() => {});
  }, []);

  const fetchCycles = () => {
    axios.get(API, { headers }).then(r => setCycles(r.data)).catch(() => {});
  };

  const openCycle = (cycle) => {
    setLoading(true);
    axios.get(`${API}/${cycle.id}`, { headers }).then(r => {
      setSelectedCycle(r.data);
      setView('detail');
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const loadObligations = (monthNumber) => {
    setSelectedMonth(monthNumber);
    axios.get(`${API}/${selectedCycle.cycle.id}/obligations/${monthNumber}`, { headers })
      .then(r => { setObligations(r.data); setView('obligations'); })
      .catch(() => {});
  };

  const showMsg = (text, type = 'success') => {
    setMsg(text); setMsgType(type);
    setTimeout(() => setMsg(''), 5000);
  };

  const createCycle = async () => {
    if (!cycleForm.cycle_number || !cycleForm.start_date)
      return showMsg('Cycle number and start date are required', 'error');
    try {
      await axios.post(API, cycleForm, { headers });
      showMsg('Financial cycle created successfully');
      setShowNewCycle(false);
      setCycleForm({ cycle_number: '', cycle_name: '', start_date: '', rosca_cycle_id: '' });
      fetchCycles();
    } catch (e) { showMsg('Error: ' + (e.response?.data?.error || e.message), 'error'); }
  };

  // ── CYCLE EDIT ─────────────────────────────────────────
  const startEditCycle = (c) => {
    setEditingCycleId(c.id);
    setEditCycleData({
      cycle_number:   c.cycle_number || '',
      cycle_name:     c.cycle_name || '',
      start_date:     c.start_date?.slice(0, 10) || '',
      rosca_cycle_id: c.rosca_cycle_id || ''
    });
  };

  const saveEditCycle = async (id) => {
    try {
      await axios.patch(`${API}/${id}`, editCycleData, { headers });
      showMsg('Cycle updated successfully');
      setEditingCycleId(null);
      fetchCycles();
      // Refresh detail view if open
      if (selectedCycle?.cycle?.id === id) {
        axios.get(`${API}/${id}`, { headers }).then(r => setSelectedCycle(r.data)).catch(() => {});
      }
    } catch (e) { showMsg('Error: ' + (e.response?.data?.error || e.message), 'error'); }
  };

  const saveSchedule = async () => {
    if (!scheduleForm.month_number) return showMsg('Month number is required', 'error');
    try {
      await axios.post(`${API}/${selectedCycle.cycle.id}/schedule`, scheduleForm, { headers });
      showMsg(`Month ${scheduleForm.month_number} schedule saved`);
      setShowScheduleForm(false);
      setScheduleForm({ month_number: '', meeting_date: '', savings_due_date: '', rosca_due_date: '', subscription_due_date: '', notes: '' });
      openCycle(selectedCycle.cycle);
    } catch (e) { showMsg('Error: ' + (e.response?.data?.error || e.message), 'error'); }
  };

  const generateObligations = async (monthNumber) => {
    try {
      const res = await axios.post(
        `${API}/${selectedCycle.cycle.id}/generate-obligations/${monthNumber}`, {}, { headers }
      );
      showMsg(res.data.message);
      openCycle(selectedCycle.cycle);
    } catch (e) { showMsg('Error: ' + (e.response?.data?.error || e.message), 'error'); }
  };

  const markOverdue = async () => {
    try {
      const res = await axios.post(`${API}/${selectedCycle.cycle.id}/mark-overdue`, {}, { headers });
      showMsg(res.data.message);
      openCycle(selectedCycle.cycle);
    } catch (e) { showMsg('Error: ' + (e.response?.data?.error || e.message), 'error'); }
  };

  const closeCycle = async () => {
    if (!window.confirm('Close this cycle? This cannot be undone.')) return;
    try {
      await axios.patch(`${API}/${selectedCycle.cycle.id}/close`, {}, { headers });
      showMsg('Cycle closed successfully');
      setView('list');
      fetchCycles();
    } catch (e) { showMsg('Error: ' + (e.response?.data?.error || e.message), 'error'); }
  };

  const getMonthLabel = (monthNumber) => {
    if (!selectedCycle?.cycle?.start_date) return `Month ${monthNumber}`;
    const start = new Date(selectedCycle.cycle.start_date);
    const d = new Date(start.getFullYear(), start.getMonth() + (monthNumber - 1), 1);
    return `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
  };

  const statusColor  = (s) => ({ active: '#27ae60', completed: '#7f8c8d', pending: '#f39c12' }[s] || '#888');
  const oblColor     = (s) => ({ paid: '#27ae60', overdue: '#e74c3c', pending: '#f39c12', due: '#e67e22' }[s] || '#888');
  const oblTypeColor = (t) => ({ savings: '#1a5276', rosca: '#8e44ad', subscription: '#27ae60', fine: '#e74c3c' }[t] || '#888');

  const inp        = { padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', width: '100%', boxSizing: 'border-box' };
  const inlineInp  = { padding: '4px 6px', border: '1px solid #f39c12', borderRadius: '4px', fontSize: '12px', background: '#fffde7', width: '100%', boxSizing: 'border-box' };
  const btn        = (bg) => ({ background: bg || '#2c3e50', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' });
  const smBtn      = (bg) => ({ background: bg || '#2c3e50', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' });
  const lbl        = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#666', marginBottom: '4px' };
  const fld        = { display: 'flex', flexDirection: 'column', minWidth: '160px' };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#2c3e50' }}>
            {view === 'list' ? 'Cycle Calendar' :
             view === 'obligations' ? `${getMonthLabel(selectedMonth)} Obligations — ${selectedCycle?.cycle?.cycle_name}` :
             selectedCycle?.cycle?.cycle_name}
          </h2>
          <p style={{ margin: '4px 0 0', color: '#888', fontSize: '13px' }}>
            {view === 'list' ? 'Manage financial cycles, monthly schedules and member obligations' :
             view === 'detail' ? `Cycle ${selectedCycle?.cycle?.cycle_number} — Started ${selectedCycle?.cycle?.start_date?.slice(0,10)}` :
             `${obligations.length} obligations for this month`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {view !== 'list' && (
            <button style={btn('#666')} onClick={() => {
              if (view === 'obligations') setView('detail');
              else { setView('list'); setSelectedCycle(null); }
            }}>← Back</button>
          )}
          {view === 'list' && canWrite && (
            <button style={btn()} onClick={() => setShowNewCycle(!showNewCycle)}>
              {showNewCycle ? 'Cancel' : '+ New Cycle'}
            </button>
          )}
          {view === 'detail' && canWrite && selectedCycle?.cycle?.status === 'active' && (
            <>
              <button style={btn('#1a5276')} onClick={() => setShowScheduleForm(!showScheduleForm)}>
                {showScheduleForm ? 'Cancel' : '+ Set Month Due Dates'}
              </button>
              <button style={btn('#e67e22')} onClick={markOverdue}>Mark Overdue</button>
              <button style={btn('#e74c3c')} onClick={closeCycle}>Close Cycle</button>
            </>
          )}
        </div>
      </div>

      {!canWrite && (
        <div style={{ background: '#eaf4fb', border: '1px solid #aed6f1', borderRadius: '6px', padding: '8px 14px', marginBottom: '16px', fontSize: '12px', color: '#1a5276' }}>
          READ-ONLY VIEW — Contact Finance to make changes
        </div>
      )}

      {msg && (
        <div style={{ padding: '10px 16px', background: msgType === 'error' ? '#fde8e8' : '#d5f5e3', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', color: msgType === 'error' ? '#c0392b' : '#1e8449' }}>
          {msg}
        </div>
      )}

      {/* ── NEW CYCLE FORM ── */}
      {showNewCycle && canWrite && (
        <div style={{ background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 16px', color: '#2c3e50' }}>Create New Financial Cycle</h4>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '14px' }}>
            <div style={fld}>
              <label style={lbl}>Cycle Number *</label>
              <input type='number' style={inp} value={cycleForm.cycle_number} onChange={e => setCycleForm({ ...cycleForm, cycle_number: e.target.value })} placeholder='e.g. 3' />
            </div>
            <div style={fld}>
              <label style={lbl}>Cycle Name</label>
              <input style={inp} value={cycleForm.cycle_name} onChange={e => setCycleForm({ ...cycleForm, cycle_name: e.target.value })} placeholder='e.g. Cycle 3 — 2026' />
            </div>
            <div style={fld}>
              <label style={lbl}>Start Date *</label>
              <input type='date' style={inp} value={cycleForm.start_date} onChange={e => setCycleForm({ ...cycleForm, start_date: e.target.value })} />
            </div>
            <div style={{ ...fld, minWidth: '220px' }}>
              <label style={lbl}>Link to ROSCA Cycle (optional)</label>
              <select style={inp} value={cycleForm.rosca_cycle_id} onChange={e => setCycleForm({ ...cycleForm, rosca_cycle_id: e.target.value })}>
                <option value=''>-- Select ROSCA Cycle --</option>
                {roscaCycles.map(r => <option key={r.id} value={r.id}>{r.cycle_name} ({r.status})</option>)}
              </select>
            </div>
          </div>
          <div style={{ background: '#eaf4fb', borderRadius: '6px', padding: '10px 14px', marginBottom: '14px', fontSize: '12px', color: '#1a5276' }}>
            ℹ After creating the cycle, set due dates for each month. The system will generate member obligations automatically.
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={btn()} onClick={createCycle}>Create Cycle</button>
            <button style={btn('#95a5a6')} onClick={() => setShowNewCycle(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── LIST VIEW ── */}
      {view === 'list' && (
        <div>
          {cycles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#aaa' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>📅</div>
              <div style={{ fontSize: '16px', marginBottom: '8px' }}>No financial cycles yet</div>
              {canWrite && <div style={{ fontSize: '13px' }}>Click <strong>+ New Cycle</strong> to set up the current cycle</div>}
            </div>
          ) : cycles.map(c => (
            <div key={c.id}>
              {editingCycleId === c.id ? (
                /* ── INLINE EDIT FORM ── */
                <div style={{ background: '#fffde7', border: '2px solid #f39c12', borderRadius: '8px', padding: '16px 20px', marginBottom: '12px' }}>
                  <div style={{ fontWeight: 'bold', color: '#2c3e50', marginBottom: '14px', fontSize: '14px' }}>
                    ✎ Editing: {c.cycle_name}
                  </div>
                  <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '14px' }}>
                    <div style={fld}>
                      <label style={lbl}>Cycle Number</label>
                      <input type='number' style={inlineInp} value={editCycleData.cycle_number}
                        onChange={e => setEditCycleData({ ...editCycleData, cycle_number: e.target.value })} />
                    </div>
                    <div style={{ ...fld, minWidth: '220px' }}>
                      <label style={lbl}>Cycle Name</label>
                      <input style={inlineInp} value={editCycleData.cycle_name}
                        onChange={e => setEditCycleData({ ...editCycleData, cycle_name: e.target.value })} />
                    </div>
                    <div style={fld}>
                      <label style={lbl}>Start Date</label>
                      <input type='date' style={inlineInp} value={editCycleData.start_date}
                        onChange={e => setEditCycleData({ ...editCycleData, start_date: e.target.value })} />
                    </div>
                    <div style={{ ...fld, minWidth: '220px' }}>
                      <label style={lbl}>Linked ROSCA Cycle</label>
                      <select style={inlineInp} value={editCycleData.rosca_cycle_id}
                        onChange={e => setEditCycleData({ ...editCycleData, rosca_cycle_id: e.target.value })}>
                        <option value=''>— None —</option>
                        {roscaCycles.map(r => <option key={r.id} value={r.id}>{r.cycle_name} ({r.status})</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button style={smBtn('#2ecc71')} onClick={() => saveEditCycle(c.id)}>Save Changes</button>
                    <button style={smBtn('#95a5a6')} onClick={() => setEditingCycleId(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                /* ── NORMAL CYCLE CARD ── */
                <div style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px 20px', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                        <span style={{ fontWeight: '700', fontSize: '16px', color: '#2c3e50' }}>{c.cycle_name}</span>
                        <span style={{ background: statusColor(c.status), color: 'white', padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                          {c.status.toUpperCase()}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#666' }}>
                        Started: {c.start_date?.slice(0, 10)}
                        {c.rosca_cycle_name && <span> | ROSCA: <strong>{c.rosca_cycle_name}</strong></span>}
                        {!c.rosca_cycle_name && <span style={{ color: '#e67e22' }}> | No ROSCA linked</span>}
                      </div>
                      <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                        {c.months_scheduled} months scheduled | {c.total_obligations} obligations | {c.paid_obligations} paid | {c.overdue_obligations} overdue
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button style={smBtn('#1a5276')} onClick={() => openCycle(c)}>View Details</button>
                      {canWrite && (
                        <button style={smBtn('#e67e22')} onClick={() => startEditCycle(c)}>Edit</button>
                      )}
                    </div>
                  </div>

                  {parseInt(c.total_obligations) > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#888', marginBottom: '4px' }}>
                        <span>Payment Progress</span>
                        <span>{Math.round(c.paid_obligations / c.total_obligations * 100)}%</span>
                      </div>
                      <div style={{ background: '#ecf0f1', borderRadius: '4px', height: '6px' }}>
                        <div style={{ background: '#27ae60', borderRadius: '4px', height: '6px', width: `${Math.min(100, c.paid_obligations / c.total_obligations * 100)}%`, transition: 'width 0.3s' }} />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── DETAIL VIEW ── */}
      {view === 'detail' && selectedCycle && (
        <div>
          {/* Cycle summary cards */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
            {[
              { label: 'Months Scheduled', value: selectedCycle.schedule.length, color: '#1a5276' },
              { label: 'Total Obligations', value: selectedCycle.cycle.total_obligations || 0, color: '#2c3e50' },
              { label: 'Paid', value: selectedCycle.cycle.paid_obligations || 0, color: '#27ae60' },
              { label: 'Overdue', value: selectedCycle.cycle.overdue_obligations || 0, color: '#e74c3c' },
            ].map(c => (
              <div key={c.label} style={{ padding: '12px 20px', background: c.color, color: 'white', borderRadius: '8px', minWidth: '130px' }}>
                <div style={{ fontSize: '11px', opacity: 0.85 }}>{c.label}</div>
                <div style={{ fontSize: '24px', fontWeight: '700' }}>{c.value}</div>
              </div>
            ))}
            {/* ROSCA link status */}
            <div style={{ padding: '12px 20px', background: selectedCycle.cycle.rosca_cycle_name ? '#8e44ad' : '#e67e22', color: 'white', borderRadius: '8px', minWidth: '160px' }}>
              <div style={{ fontSize: '11px', opacity: 0.85 }}>Linked ROSCA</div>
              <div style={{ fontSize: '14px', fontWeight: '700', marginTop: '4px' }}>
                {selectedCycle.cycle.rosca_cycle_name || 'None linked'}
              </div>
            </div>
          </div>

          {/* Set month due dates form */}
          {showScheduleForm && canWrite && (
            <div style={{ background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
              <h4 style={{ margin: '0 0 16px', color: '#2c3e50' }}>Set Monthly Due Dates</h4>
              <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '14px' }}>
                <div style={fld}>
                  <label style={lbl}>Month Number *</label>
                  <input type='number' min='1' max='21' style={inp} value={scheduleForm.month_number}
                    onChange={e => setScheduleForm({ ...scheduleForm, month_number: e.target.value })} placeholder='1–21' />
                </div>
                <div style={fld}>
                  <label style={lbl}>Meeting Date</label>
                  <input type='date' style={inp} value={scheduleForm.meeting_date}
                    onChange={e => setScheduleForm({ ...scheduleForm, meeting_date: e.target.value })} />
                </div>
                <div style={fld}>
                  <label style={lbl}>Savings Due Date</label>
                  <input type='date' style={inp} value={scheduleForm.savings_due_date}
                    onChange={e => setScheduleForm({ ...scheduleForm, savings_due_date: e.target.value })} />
                </div>
                <div style={fld}>
                  <label style={lbl}>ROSCA Contribution Due</label>
                  <input type='date' style={inp} value={scheduleForm.rosca_due_date}
                    onChange={e => setScheduleForm({ ...scheduleForm, rosca_due_date: e.target.value })} />
                </div>
                <div style={fld}>
                  <label style={lbl}>Subscription Due Date</label>
                  <input type='date' style={inp} value={scheduleForm.subscription_due_date}
                    onChange={e => setScheduleForm({ ...scheduleForm, subscription_due_date: e.target.value })} />
                </div>
                <div style={{ ...fld, minWidth: '220px' }}>
                  <label style={lbl}>Notes</label>
                  <input style={inp} value={scheduleForm.notes}
                    onChange={e => setScheduleForm({ ...scheduleForm, notes: e.target.value })} placeholder='Optional notes for this month' />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={btn()} onClick={saveSchedule}>Save Due Dates</button>
                <button style={btn('#95a5a6')} onClick={() => setShowScheduleForm(false)}>Cancel</button>
              </div>
            </div>
          )}

          {/* Monthly schedule table */}
          <h3 style={{ color: '#2c3e50', marginBottom: '12px' }}>Monthly Schedule</h3>
          {selectedCycle.schedule.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#aaa', background: '#f9f9f9', borderRadius: '8px' }}>
              {canWrite ? 'No months scheduled yet — click + Set Month Due Dates to begin' : 'No months scheduled yet'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#2c3e50', color: 'white' }}>
                    {['Month', 'Meeting Date', 'Savings Due', 'ROSCA Due', 'Subscription Due', 'Obligations', 'Paid', 'Overdue', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px', textAlign: 'left', fontSize: '12px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selectedCycle.schedule.map((s, i) => (
                    <tr key={s.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white', borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '10px', fontWeight: '700', color: '#2c3e50' }}>{getMonthLabel(s.month_number)}</td>
                      <td style={{ padding: '10px' }}>{s.meeting_date?.slice(0, 10) || '—'}</td>
                      <td style={{ padding: '10px', color: '#1a5276', fontWeight: '600' }}>{s.savings_due_date?.slice(0, 10) || '—'}</td>
                      <td style={{ padding: '10px', color: '#8e44ad', fontWeight: '600' }}>{s.rosca_due_date?.slice(0, 10) || '—'}</td>
                      <td style={{ padding: '10px', color: '#27ae60', fontWeight: '600' }}>{s.subscription_due_date?.slice(0, 10) || '—'}</td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>{s.obligations_count || 0}</td>
                      <td style={{ padding: '10px', textAlign: 'center', color: '#27ae60', fontWeight: '600' }}>{s.paid_count || 0}</td>
                      <td style={{ padding: '10px', textAlign: 'center', color: parseInt(s.overdue_count) > 0 ? '#e74c3c' : '#aaa', fontWeight: parseInt(s.overdue_count) > 0 ? '700' : 'normal' }}>{s.overdue_count || 0}</td>
                      <td style={{ padding: '10px' }}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => loadObligations(s.month_number)} style={smBtn('#1a5276')}>View</button>
                          {canWrite && parseInt(s.obligations_count) === 0 && (
                            <button onClick={() => generateObligations(s.month_number)} style={smBtn('#27ae60')}>Generate</button>
                          )}
                          {canWrite && (
                            <button onClick={() => {
                              setScheduleForm({
                                month_number: s.month_number,
                                meeting_date: s.meeting_date?.slice(0,10) || '',
                                savings_due_date: s.savings_due_date?.slice(0,10) || '',
                                rosca_due_date: s.rosca_due_date?.slice(0,10) || '',
                                subscription_due_date: s.subscription_due_date?.slice(0,10) || '',
                                notes: s.notes || ''
                              });
                              setShowScheduleForm(true);
                            }} style={smBtn('#e67e22')}>Edit</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {selectedCycle.cycle.status === 'active' && canWrite && (
            <div style={{ marginTop: '16px', padding: '12px 16px', background: '#fef9e7', border: '1px solid #f9ca3a', borderRadius: '6px', fontSize: '12px', color: '#7d6608' }}>
              <strong>Workflow:</strong> Set due dates for each month → Click Generate to create member obligations →
              Use <strong>Mark Overdue</strong> to flag unpaid obligations past their due date →
              Close cycle only when the last member receives their payout.
            </div>
          )}
        </div>
      )}

      {/* ── OBLIGATIONS VIEW ── */}
      {view === 'obligations' && (
        <div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
            {['savings', 'rosca', 'subscription'].map(type => {
              const typeObls = obligations.filter(o => o.obligation_type === type);
              if (!typeObls.length) return null;
              const paid    = typeObls.filter(o => o.status === 'paid').length;
              const overdue = typeObls.filter(o => o.status === 'overdue').length;
              const pending = typeObls.filter(o => o.status === 'pending').length;
              return (
                <div key={type} style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '12px 16px', minWidth: '180px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                    <span style={{ background: oblTypeColor(type), color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>{type}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    <div>Total: <strong>{typeObls.length}</strong></div>
                    <div style={{ color: '#27ae60' }}>Paid: <strong>{paid}</strong></div>
                    <div style={{ color: '#e74c3c' }}>Overdue: <strong>{overdue}</strong></div>
                    <div style={{ color: '#f39c12' }}>Pending: <strong>{pending}</strong></div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#2c3e50', color: 'white' }}>
                  {['Member', 'Type', 'Due Date', 'Amount (UGX)', 'Paid (UGX)', 'Status'].map(h => (
                    <th key={h} style={{ padding: '10px', textAlign: 'left', fontSize: '12px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {obligations.length === 0 ? (
                  <tr><td colSpan='6' style={{ padding: '40px', textAlign: 'center', color: '#aaa' }}>No obligations for this month</td></tr>
                ) : obligations.map((o, i) => (
                  <tr key={o.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white', borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '10px' }}>
                      <div style={{ fontWeight: '600' }}>{o.full_name}</div>
                      <div style={{ color: '#888', fontSize: '11px' }}>{o.member_number}</div>
                    </td>
                    <td style={{ padding: '10px' }}>
                      <span style={{ background: oblTypeColor(o.obligation_type), color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>
                        {o.obligation_type}
                      </span>
                    </td>
                    <td style={{ padding: '10px', color: o.status === 'overdue' ? '#e74c3c' : '#444', fontWeight: o.status === 'overdue' ? '700' : 'normal' }}>
                      {o.due_date?.slice(0, 10)}
                    </td>
                    <td style={{ padding: '10px', fontWeight: '600' }}>{Number(o.amount).toLocaleString()}</td>
                    <td style={{ padding: '10px', color: '#27ae60', fontWeight: '600' }}>
                      {parseFloat(o.paid_amount) > 0 ? Number(o.paid_amount).toLocaleString() : '—'}
                    </td>
                    <td style={{ padding: '10px' }}>
                      <span style={{ background: oblColor(o.status), color: 'white', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                        {o.status.toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default FinancialCycles;